import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  TruckIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  DocumentIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { vehiclesService } from '../services/logistics/vehicles';
import { truckTypesService } from '../services/logistics/truck-types';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import TruckTypeManagementModal from '../components/vehicles/TruckTypeManagementModal';
import OdometerReadingModal from '../components/vehicles/OdometerReadingModal';
import OdometerHistoryModal from '../components/vehicles/OdometerHistoryModal';

const Vehicles = () => {
  const { t, i18n } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  const navigate = useNavigate();

  // Truck types state
  const [truckTypes, setTruckTypes] = useState([]);
  const [truckTypesLoading, setTruckTypesLoading] = useState(true);
  const [showTruckTypeModal, setShowTruckTypeModal] = useState(false);
  
  // Odometer modals state
  const [showOdometerModal, setShowOdometerModal] = useState(false);
  const [showOdometerHistory, setShowOdometerHistory] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return t('common.not_specified', 'Not specified');
    
    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }
    
    return truckTypeValue;
  };

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    plateNumber: '',
    make: '',
    model: '',
    year: '',
    truckType: '',
    capacity: '',
    color: '',
    vinNumber: '',
    insuranceProvider: '',
    insuranceNumber: '',
    insuranceExpiry: '',
    registrationNumber: '',
    registrationExpiry: '',
    status: 'active',
    notes: '',
    initialOdometer: ''
  });

  const loadTruckTypes = useCallback(async () => {
    try {
      setTruckTypesLoading(true);
      const types = await truckTypesService.getTruckTypes(currentCompany.id);
      setTruckTypes(types);
    } catch (error) {
      console.error('Error loading truck types:', error);
      toast.error(t('vehicles.failed_to_load_truck_types', 'Failed to load truck types'));
    } finally {
      setTruckTypesLoading(false);
    }
  }, [currentCompany?.id, t]);

  const loadVehicles = useCallback(async () => {
    try {
      const vehiclesData = await vehiclesService.getVehicles(currentCompany.id);
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error(t('vehicles.failed_to_load', 'Failed to load vehicles'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  useEffect(() => {
    if (currentCompany?.id) {
      loadVehicles();
      loadTruckTypes();
    }
  }, [currentCompany?.id, loadVehicles, loadTruckTypes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.plateNumber || !formData.make || !formData.model) {
      toast.error(t('common.required_field'));
      return;
    }

    if (!currentCompany?.id) {
      toast.error(t('common.company_not_loaded', 'Company information is not available.'));
      return;
    }

    try {
      const vehicleData = {
        ...formData,
        companyId: currentCompany.id,
        createdBy: currentUser?.uid
      };

      if (editingVehicle) {
        await vehiclesService.updateVehicle(editingVehicle.id, vehicleData);
        toast.success(t('vehicles.vehicle_updated_success'));
      } else {
        await vehiclesService.addVehicle(vehicleData);
        toast.success(t('vehicles.vehicle_created_success'));
      }

      setIsModalOpen(false);
      setEditingVehicle(null);
      resetForm();
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(t('vehicles.failed_to_save', 'Failed to save vehicle'));
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      plateNumber: vehicle.plateNumber || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      truckType: vehicle.truckType || vehicle.type || '',
      capacity: vehicle.capacity || '',
      color: vehicle.color || '',
      vinNumber: vehicle.vinNumber || '',
      insuranceProvider: vehicle.insuranceProvider || '',
      insuranceNumber: vehicle.insuranceNumber || '',
      insuranceExpiry: vehicle.insuranceExpiry || '',
      registrationNumber: vehicle.registrationNumber || '',
      registrationExpiry: vehicle.registrationExpiry || '',
      status: vehicle.status || 'active',
      notes: vehicle.notes || '',
      initialOdometer: ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (vehicleId) => {
    if (window.confirm(t('vehicles.delete_confirmation', 'Are you sure you want to delete this vehicle?'))) {  
      try {
        await vehiclesService.deleteVehicle(vehicleId);
        toast.success(t('vehicles.vehicle_deleted_success'));
        loadVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        toast.error(t('vehicles.failed_to_delete', 'Failed to delete vehicle'));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      plateNumber: '',
      make: '',
      model: '',
      year: '',
      truckType: '',
      capacity: '',
      color: '',
      vinNumber: '',
      insuranceProvider: '',
      insuranceNumber: '',
      insuranceExpiry: '',
      registrationNumber: '',
      registrationExpiry: '',
      status: 'active',
      notes: '',
      initialOdometer: ''
    });
  };

  // Odometer handlers
  const handleAddOdometerReading = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowOdometerModal(true);
  };

  const handleViewOdometerHistory = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowOdometerHistory(true);
  };

  const handleOdometerReadingAdded = () => {
    loadVehicles(); // Refresh vehicles to get updated odometer data
  };



  const getExpirationStatus = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', days: Math.abs(daysUntilExpiry), color: 'text-red-600' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', days: daysUntilExpiry, color: 'text-yellow-600' };
    } else {
      return { status: 'valid', days: daysUntilExpiry, color: 'text-green-600' };
    }
  };

  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('vehicles.title')}</h1>
          <p className="text-gray-600">{t('vehicles.subtitle', 'Manage your company vehicles and track document expirations')}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowTruckTypeModal(true)}
            className="btn-secondary flex items-center space-x-2"
            title={t('vehicles.manage_truck_types', 'Manage Truck Types')}
          >
            <Cog6ToothIcon className="h-5 w-5" />
            <span>{t('vehicles.truck_types', 'Truck Types')}</span>
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingVehicle(null);
              setIsModalOpen(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>{t('vehicles.add_vehicle')}</span>
          </button>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-1">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('vehicles.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>
        
        <div className="stats-card">
          <div className="flex items-center">
            <TruckIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">{t('vehicles.total_vehicles')}</p>
              <p className="text-2xl font-bold">{vehicles.length}</p>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center">
            <TruckIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">{t('vehicles.active_vehicles')}</p>
              <p className="text-2xl font-bold">{vehicles.filter(v => v.status === 'active').length}</p>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">{t('vehicles.expiring_documents')}</p>
              <p className="text-2xl font-bold">
                {vehicles.filter(vehicle => {
                  const insuranceStatus = getExpirationStatus(vehicle.insuranceExpiry);
                  const registrationStatus = getExpirationStatus(vehicle.registrationExpiry);
                  return (insuranceStatus?.status === 'expiring' || insuranceStatus?.status === 'expired') ||
                         (registrationStatus?.status === 'expiring' || registrationStatus?.status === 'expired');
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="card">
        <div className="card-body">
          {vehicles.length === 0 ? (
            <div className="text-center py-12">
              <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('vehicles.no_vehicles_found')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('vehicles.get_started_first_vehicle')}</p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    resetForm();
                    setEditingVehicle(null);
                    setIsModalOpen(true);
                  }}
                  className="btn-primary"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {t('vehicles.add_vehicle')}
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">{t('vehicles.plate_number')}</th>
                    <th className="table-header">{t('common.details')}</th>
                    <th className="table-header">{t('vehicles.odometer', 'Odometer')}</th>
                    <th className="table-header">{t('vehicles.insurance_information')}</th>
                    <th className="table-header">{t('vehicles.registration_information')}</th>
                    <th className="table-header">{t('vehicles.status')}</th>
                    <th className="table-header">{t('common.actions')}</th>
                  </tr>
                </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVehicles.map((vehicle) => {
                  const insuranceStatus = getExpirationStatus(vehicle.insuranceExpiry);
                  const registrationStatus = getExpirationStatus(vehicle.registrationExpiry);

                  return (
                    <tr key={vehicle.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/vehicles/${vehicle.id}`)}>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <TruckIcon className="h-6 w-6 text-gray-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 hover:text-blue-600">{vehicle.plateNumber}</div>
                            <div className="text-sm text-gray-500">{vehicle.make} {vehicle.model}</div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          <div>
                            {vehicle.year}
                            {(vehicle.truckType || vehicle.type) && ` • ${getTruckTypeName(vehicle.truckType || vehicle.type)}`}
                          </div>
                          <div className="text-gray-500">
                            {vehicle.capacity && `${vehicle.capacity} capacity`}
                            {vehicle.color && ` • ${vehicle.color}`}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        {vehicle.latestOdometerReading ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {vehicle.latestOdometerReading.reading.toLocaleString()} km
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(vehicle.latestOdometerReading.recordedAt).toLocaleDateString()}
                            </div>
                            <div className="flex space-x-1 mt-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddOdometerReading(vehicle);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                {t('vehicles.add_reading', 'Add')}
                              </button>
                              <span className="text-xs text-gray-300">|</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewOdometerHistory(vehicle);
                                }}
                                className="text-xs text-green-600 hover:text-green-800"
                              >
                                {t('vehicles.history', 'History')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm">
                            <div className="text-gray-400 mb-1">
                              {t('vehicles.no_readings', 'No readings')}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddOdometerReading(vehicle);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {t('vehicles.add_first_reading', 'Add first reading')}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        {vehicle.insuranceExpiry ? (
                          <div className={`text-sm ${insuranceStatus?.color}`}>
                            <div className="flex items-center">
                              <ShieldCheckIcon className="h-4 w-4 mr-1" />
                              {new Date(vehicle.insuranceExpiry).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                calendar: 'gregory'
                              })}
                            </div>
                            <div className="text-xs">
                              {insuranceStatus?.status === 'expired' 
                                ? t('common.expired_days_ago', { days: insuranceStatus.days })
                                : insuranceStatus?.status === 'expiring'
                                ? t('common.expires_in_days', { days: insuranceStatus.days })
                                : t('common.valid_days', { days: insuranceStatus.days })
                              }
                            </div>
                            {vehicle.insuranceProvider && (
                              <div className="text-xs text-gray-500">{vehicle.insuranceProvider}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="table-cell">
                        {vehicle.registrationExpiry ? (
                          <div className={`text-sm ${registrationStatus?.color}`}>
                            <div className="flex items-center">
                              <DocumentIcon className="h-4 w-4 mr-1" />
                              <span className={getExpirationStatus(vehicle.registrationExpiry)?.status === 'expired' ? 'text-red-600' : getExpirationStatus(vehicle.registrationExpiry)?.status === 'expiring' ? 'text-yellow-600' : 'text-gray-900'}>
                                {new Date(vehicle.registrationExpiry).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="text-xs">
                              {registrationStatus?.status === 'expired' 
                                ? t('common.expired_days_ago', { days: registrationStatus.days })
                                : registrationStatus?.status === 'expiring'
                                ? t('common.expires_in_days', { days: registrationStatus.days })
                                : t('common.valid_days', { days: registrationStatus.days })
                              }
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">{t('common.not_set')}</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <span className={`status-badge ${vehicle.status === 'active' ? 'status-badge-success' : 'status-badge-warning'}`}>
                          {t(`vehicles.${vehicle.status}`)}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(vehicle);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(vehicle.id);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Vehicle Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingVehicle ? t('vehicles.edit_vehicle') : t('vehicles.add_new_vehicle')}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">{t('vehicles.plate_number')} *</label>
                  <input
                    type="text"
                    name="plateNumber"
                    value={formData.plateNumber}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label">{t('vehicles.make')} *</label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label">{t('vehicles.model')} *</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">{t('vehicles.year')}</label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className="input-field"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                </div>
                <div>
                  <label className="label">{t('vehicles.type')}</label>
                  <select
                    name="truckType"
                    value={formData.truckType}
                    onChange={handleInputChange}
                    className="input-field"
                    disabled={truckTypesLoading}
                  >
                    <option value="">{truckTypesLoading ? t('common.loading', t('common.loading')) : t('vehicles.select_type', 'Select Type')}</option>
                    {truckTypes.map((truckType) => (
                      <option key={truckType.value} value={truckType.value}>
                        {truckType.name[i18n.language] || truckType.name.en}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t('vehicles.capacity')}</label>
                  <input
                    type="text"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder={t('vehicles.capacity_placeholder', 'e.g., 5 tons')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('vehicles.color')}</label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">{t('vehicles.vin_number')}</label>
                  <input
                    type="text"
                    name="vinNumber"
                    value={formData.vinNumber}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">{t('vehicles.insurance_information')}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="label">{t('vehicles.insurance_provider')}</label>
                    <input
                      type="text"
                      name="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="label">{t('vehicles.insurance_number')}</label>
                    <input
                      type="text"
                      name="insuranceNumber"
                      value={formData.insuranceNumber}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="label">{t('vehicles.insurance_expiry')}</label>
                    <input
                      type="date"
                      name="insuranceExpiry"
                      value={formData.insuranceExpiry}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">{t('vehicles.registration_information')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('vehicles.registration_number')}</label>
                    <input
                      type="text"
                      name="registrationNumber"
                      value={formData.registrationNumber}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="label">{t('vehicles.registration_expiry')}</label>
                    <input
                      type="date"
                      name="registrationExpiry"
                      value={formData.registrationExpiry}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('vehicles.status')}</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="active">{t('vehicles.active')}</option>
                    <option value="maintenance">{t('vehicles.maintenance')}</option>
                    <option value="inactive">{t('vehicles.inactive')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">{t('vehicles.notes')}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="input-field"
                  rows={3}
                />
              </div>

              {!editingVehicle && (
                <div>
                  <label className="label">
                    {t('vehicles.initial_odometer', 'Initial Odometer Reading')} (km)
                  </label>
                  <input
                    type="number"
                    name="initialOdometer"
                    value={formData.initialOdometer}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder={t('vehicles.initial_odometer_placeholder', 'Enter current odometer reading')}
                    min="0"
                    step="0.1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('vehicles.initial_odometer_help', 'Optional: Set the current odometer reading for this vehicle')}
                  </p>
                </div>
              )}



              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingVehicle ? t('common.update') : t('vehicles.add_vehicle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Truck Type Management Modal */}
      <TruckTypeManagementModal
        isOpen={showTruckTypeModal}
        onClose={() => setShowTruckTypeModal(false)}
        companyId={currentCompany?.id}
        onTruckTypesUpdated={loadTruckTypes}
      />

      {/* Odometer Reading Modal */}
      <OdometerReadingModal
        isOpen={showOdometerModal}
        onClose={() => setShowOdometerModal(false)}
        vehicle={selectedVehicle}
        onReadingAdded={handleOdometerReadingAdded}
      />

      {/* Odometer History Modal */}
      <OdometerHistoryModal
        isOpen={showOdometerHistory}
        onClose={() => setShowOdometerHistory(false)}
        vehicle={selectedVehicle}
      />

    </div>
  );
};

export default Vehicles; 